import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { STATEMENT_STATUS_LABEL, partnerArApi, type PartnerStatement } from "@/services/partnerAr";
import { formatCents } from "@/services/finance";
import { Can } from "@/auth/permissions";
import { Pill } from "@/shared/ui/PageHeader";

/**
 * 对账单（后台）— 样稿 credit-mockup-v1 ④。月结同行每月一张（每月 1 号 08:00 太平洋自动出上月）；单结 / 预付同行不出。
 * 对账单是某一时刻的快照（单数 / 金额生成时冻结），已收 / 待收实时读应收表。
 * 「PDF」= 打印友好页（浏览器「打印 / 存 PDF」），邮件正文带逐单明细、不带附件。
 */
const TONE: Record<string, "info" | "success" | "danger" | "muted"> = { draft: "muted", sent: "info", paid: "success", overdue: "danger" };
const md = (iso: string | null) => (iso ? iso.slice(5) : "—");

function statusText(s: PartnerStatement): string {
  if (s.status === "sent" && s.sent_at) return `已发送 ${s.sent_at.slice(5, 10)}`;
  return STATEMENT_STATUS_LABEL[s.status];
}

export function PartnerStatementsCard({ partnerId }: { partnerId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["b2b", "statements", partnerId], queryFn: () => partnerArApi.statements(partnerId) });
  const [viewId, setViewId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["b2b", "statements", partnerId] }); qc.invalidateQueries({ queryKey: ["b2b", "settlement-board"] }); };

  const gen = useMutation({
    mutationFn: () => partnerArApi.generateStatement(partnerId),
    onSuccess: (st) => { refresh(); setNotice(`已生成 ${st.number}（${st.orders_count} 单 · ${formatCents(st.total_cents)}）`); },
    onError: (e) => setNotice((e as Error).message || "生成对账单失败"),
  });
  const send = useMutation({
    mutationFn: (sid: string) => partnerArApi.sendStatement(partnerId, sid),
    onSuccess: (r) => { refresh(); setNotice(`已发送到 ${r.sent_to.join("、")}${r.failed.length ? `；失败：${r.failed.join("；")}` : ""}`); },
    onError: (e) => setNotice((e as Error).message || "发送失败"),
  });
  const print = (sid: string) => partnerArApi.openStatementPrint(partnerId, sid).catch((e) => setNotice((e as Error).message || "打开打印页失败"));

  const list = q.data ?? [];
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="statements-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-[15px] font-semibold text-slate-800">对账单</h3>
        <span className="text-[12px] text-slate-400">月结同行每月一张 · 单结同行不出（逐单到期）</span>
        <Can perm="finance.receivables.manage">
          <button type="button" disabled={gen.isPending} onClick={() => gen.mutate()} data-testid="statement-generate" className="ml-auto h-8 rounded-lg border border-slate-300 bg-white px-3 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {gen.isPending ? "生成中…" : "手动重新生成本月"}
          </button>
        </Can>
      </div>
      {notice && <div className="mb-2 rounded-lg bg-slate-50 px-3 py-1.5 text-[12px] text-slate-600" role="status">{notice}</div>}

      {q.isLoading ? <div className="py-6 text-center text-[12px] text-slate-400">加载中…</div>
        : q.isError ? <div className="py-6 text-center text-[12px] text-slate-500">对账单加载失败 <button type="button" onClick={() => q.refetch()} className="ml-2 text-blue-600 hover:underline">重试</button></div>
        : (
          <div className="overflow-x-auto rounded-[10px] border border-slate-200">
            <table className="w-full min-w-[760px] text-[13px] tabular-nums">
              <thead className="bg-slate-50 text-[12px] font-medium text-slate-400">
                <tr>
                  <th className="px-2.5 py-2 text-left">对账单号</th><th className="px-2.5 py-2 text-left">期间（按出发日）</th><th className="px-2.5 py-2 text-right">单数</th>
                  <th className="px-2.5 py-2 text-right">应收</th><th className="px-2.5 py-2 text-right">已收</th><th className="px-2.5 py-2 text-right">待收</th>
                  <th className="px-2.5 py-2 text-left">到期</th><th className="px-2.5 py-2 text-left">状态</th><th className="px-2.5 py-2" />
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid="statement-row">
                    <td className="px-2.5 py-2">{s.number}</td>
                    <td className="px-2.5 py-2">{s.period}</td>
                    <td className="px-2.5 py-2 text-right">{s.orders_count}</td>
                    <td className="px-2.5 py-2 text-right">{formatCents(s.total_cents)}</td>
                    <td className="px-2.5 py-2 text-right">{formatCents(s.paid_cents)}</td>
                    <td className="px-2.5 py-2 text-right font-medium">{formatCents(s.unpaid_cents)}</td>
                    <td className="px-2.5 py-2">{md(s.due_date)}</td>
                    <td className="px-2.5 py-2"><Pill tone={TONE[s.status]}>{statusText(s)}</Pill></td>
                    <td className="whitespace-nowrap px-2.5 py-2 text-right">
                      <button type="button" onClick={() => setViewId(s.id)} className="mr-1 h-7 rounded-lg border border-slate-300 px-2.5 text-[12px] text-slate-700 hover:bg-slate-50">查看</button>
                      <button type="button" onClick={() => print(s.id)} className="mr-1 h-7 rounded-lg border border-slate-300 px-2.5 text-[12px] text-slate-700 hover:bg-slate-50">打印 / 存 PDF</button>
                      <Can perm="finance.receivables.manage">
                        <button type="button" disabled={send.isPending} onClick={() => send.mutate(s.id)} className="h-7 rounded-lg border border-slate-300 px-2.5 text-[12px] text-slate-700 hover:bg-slate-50 disabled:opacity-50">{s.sent_at ? "重发邮件" : "发送邮件"}</button>
                      </Can>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400">还没有对账单（月结同行每月 1 号自动出上月；也可手动生成本月）</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      <div className="mt-2 text-[11.5px] text-slate-400">对账单是某一时刻的快照（单数 / 金额生成时冻结），已收 / 待收实时读应收表；生成后发邮件给同行（正文带逐单明细和同行站链接）· 每月 1 号 08:00（太平洋）定时生成。</div>

      {viewId && <StatementView partnerId={partnerId} sid={viewId} onClose={() => setViewId(null)} onPrint={() => print(viewId)} />}
    </section>
  );
}

function StatementView({ partnerId, sid, onClose, onPrint }: { partnerId: string; sid: string; onClose: () => void; onPrint: () => void }) {
  const q = useQuery({ queryKey: ["b2b", "statement", partnerId, sid], queryFn: () => partnerArApi.statement(partnerId, sid) });
  const s = q.data;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/45 p-4 pt-[6vh]" onClick={onClose}>
      <div role="dialog" aria-label="对账单明细" data-testid="statement-view" className="w-full max-w-[820px] rounded-[14px] bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {!s ? <div className="py-8 text-center text-[13px] text-slate-400">{q.isError ? "加载失败" : "加载中…"}</div> : (
          <>
            <div className="flex items-start gap-2">
              <div>
                <h4 className="text-[17px] font-semibold text-slate-900">{s.number}</h4>
                <div className="text-[12.5px] text-slate-500">{s.period.replace("-", " 年 ")} 月（按出发日期）· 到期 {s.due_date ?? "—"} · {s.generated_by === "auto" ? "系统自动生成" : "手动生成"}</div>
              </div>
              <button type="button" aria-label="关闭" onClick={onClose} className="ml-auto text-[18px] text-slate-400">✕</button>
            </div>
            <div className="my-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[["单数", String(s.orders_count)], ["应收", formatCents(s.total_cents)], ["已收", formatCents(s.paid_cents)], ["待收", formatCents(s.unpaid_cents)]].map(([l, v]) => (
                <div key={l} className="rounded-[10px] border border-slate-100 bg-slate-50 px-3 py-2"><div className="text-[12px] text-slate-400">{l}</div><div className="text-[16px] font-semibold tabular-nums">{v}</div></div>
              ))}
            </div>
            <div className="max-h-[50vh] overflow-auto rounded-[10px] border border-slate-200">
              <table className="w-full min-w-[640px] text-[13px] tabular-nums">
                <thead className="bg-slate-50 text-[12px] font-medium text-slate-400">
                  <tr><th className="px-2.5 py-2 text-left">出发</th><th className="px-2.5 py-2 text-left">订单号</th><th className="px-2.5 py-2 text-left">同行订单号</th><th className="px-2.5 py-2 text-left">产品</th><th className="px-2.5 py-2 text-right">人数</th><th className="px-2.5 py-2 text-right">应付</th><th className="px-2.5 py-2 text-right">已付</th></tr>
                </thead>
                <tbody>
                  {(s.lines ?? []).map((l) => (
                    <tr key={l.receivable_id} className={`border-t border-slate-100 ${l.voided ? "text-slate-400 line-through" : ""}`}>
                      <td className="px-2.5 py-1.5">{l.travel_date.slice(5)}</td><td className="px-2.5 py-1.5">{l.order_no}</td><td className="px-2.5 py-1.5">{l.partner_ref ?? "—"}</td>
                      <td className="px-2.5 py-1.5">{l.product_name ?? "—"}</td><td className="px-2.5 py-1.5 text-right">{l.pax_count}</td>
                      <td className="px-2.5 py-1.5 text-right">{formatCents(l.amount_cents)}</td><td className="px-2.5 py-1.5 text-right">{formatCents(l.paid_cents ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-1.5 text-[11.5px] text-slate-400">划线的行 = 对账单生成后订单被取消 / 作废（待收已不含它）；点「手动重新生成」可刷新快照。</div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => partnerArApi.downloadStatementCsv(partnerId, sid, s.number).catch((e) => alert((e as Error).message))} className="h-9 rounded-lg border border-slate-300 px-4 text-[13px] text-slate-700">导出 CSV</button>
              <button type="button" onClick={onPrint} className="h-9 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white">打印 / 存 PDF</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
